import { lazy } from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { Routes } from "components/Sentry/Routes";
import Constants from "config/Constants";
import RemoteConfigProvider from "providers/RemoteConfigProvider";
import AuthVerifierProvider from "providers/AuthVerifierProvider";
import AppRoutes from "./AppRoutes";
import AuthenticatedShell from "./AppInitialiser/components/AuthenticatedShell";
import NeutralShell from "./AppInitialiser/components/NeutralShell";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";

const HyroxAccess = lazy(() => import("features/HyroxAccess"));
const Invite = lazy(() => import("features/Invite"));
const VerifyEmail = lazy(() => import("features/VerifyEmail"));
const Connect = lazy(() => import("features/Connect"));
const IntegrationConnect = lazy(() => import("features/IntegrationConnect"));

/**
 * Main application component that sets up the routing and global providers.
 *
 * The whole app shares a single router, and each branch of the route tree decides what
 * authentication it needs:
 *
 * - NeutralShell holds the routes that handle a token from an email link. They are
 *   reachable signed in or signed out.
 * - AuthenticatedShell guards everything else, rendering the login screen for signed out
 *   visitors and mounting the app provider stack once a user is verified.
 * - AuthVerifierProvider owns the single Firebase auth listener the shells read from.
 */
function App() {
  return (
    <AuthVerifierProvider>
      <Router>
        <NuqsAdapter>
          <RemoteConfigProvider>
            <Routes>
              <Route element={<NeutralShell />}>
                <Route path={Constants.routes.hyrox.route} element={<HyroxAccess />} />
                <Route path={Constants.routes.join.route} element={<Invite />} />
                <Route path={`${Constants.routes.verifyEmail.route}/:token?`} element={<VerifyEmail />} />
                <Route path={Constants.routes.connect.route} element={<Connect />} />
                <Route path={Constants.routes.integrationConnect.route} element={<IntegrationConnect />} />
              </Route>
              <Route path="/*" element={<AuthenticatedShell />}>
                <Route path="*" element={<AppRoutes />} />
              </Route>
            </Routes>
          </RemoteConfigProvider>
        </NuqsAdapter>
      </Router>
    </AuthVerifierProvider>
  );
}

export default App;
